import { NavLink, useLocation } from "react-router-dom";
import { useIsMobile } from "@lyder-talk/ui";
import { cn } from "@/lib/utils";
import { isNavItemActive, MOBILE_PRIMARY } from "@/components/layout/nav-config";
import { useNavLabels } from "@/components/layout/useNavLabels";
import { useGuardedNavigate } from "@/hooks/use-guarded-navigate";
import { useTranslation } from "react-i18next";

/**
 * Routes where a bottom tab bar would fight the page for space. The app-shell
 * routes bring their own side rail, practice is a locked-viewport surface, and
 * auth has no navigation at all.
 */
const HIDE_PREFIXES = ["/my-topics", "/auth", "/vocabulary/practice"];

/**
 * The mobile tab bar.
 *
 * Its four slots come from `MOBILE_PRIMARY` in `nav-config`, so this surface
 * can no longer drift from the desktop rail. Active state uses the same
 * `isNavItemActive` predicate, which means "Browse" stays lit on
 * `/topics/trending` here exactly as it does in the sidebar.
 */
export function MobileBottomNav() {
  const isMobile = useIsMobile();
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const { itemLabel } = useNavLabels();
  const guardedNavigate = useGuardedNavigate();

  if (!isMobile) return null;
  if (HIDE_PREFIXES.some((p) => pathname.startsWith(p))) return null;
  if (/^\/session\/.+\/practice/.test(pathname)) return null;

  return (
    <nav
        aria-label={t("nav.mobile.tabBar", "Main navigation")}
        className="fixed bottom-0 left-0 right-0 z-50 flex h-14 items-center justify-around border-t border-stone-200 bg-background/95 px-4 backdrop-blur-md md:hidden dark:border-stone-800"
      >
      {MOBILE_PRIMARY.map((item) => {
        const active = isNavItemActive(pathname, item);
        const Icon = item.icon;
        return (
          <NavLink
            key={item.id}
            to={item.path}
            /*
             * Both the default and the guarded path go through
             * `guardedNavigate`; it navigates straight away when the
             * destination is public or the learner is signed in, and opens the
             * gate instead when it is not. `My Vocabulary` is the one slot in
             * `MOBILE_PRIMARY` that needs an account.
             */
            onClick={(event) => {
              event.preventDefault();
              void guardedNavigate(item.path);
            }}
            className={cn(
              "relative flex flex-col items-center gap-0.5 rounded-lg px-3 py-1 transition-colors duration-200",
              active ? "text-primary" : "text-stone-500 dark:text-stone-400"
            )}
            aria-current={active ? "page" : undefined}
          >
            <Icon className="h-5 w-5" aria-hidden="true" />
            <span className="text-[11px] font-medium">{itemLabel(item)}</span>
            {active && (
              <span
                className="absolute -bottom-1.5 left-1/2 h-0.5 w-4 -translate-x-1/2 rounded-full bg-primary"
                aria-hidden="true"
              />
            )}
          </NavLink>
        );
      })}
    </nav>
  );
}

export default MobileBottomNav;